// Typed fetch wrappers for the Worker API.
// Vite proxies /api/* to the worker in dev; all paths work as-is in production.

import type { AlertLevel, SiteHistory, GaugeProperties } from "../../shared/types.ts";

export { GaugeType, DisplayType, Measurement, RAINFALL_MEASUREMENTS, RIVER_MEASUREMENTS } from "../../shared/types.ts";
export type { AlertLevel, SiteHistory, GaugeProperties };

export type GaugeFeature = GeoJSON.Feature<GeoJSON.Point, GaugeProperties>;
export type GaugeFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Point, GaugeProperties>;

export type GaugeHistoryPoint = {
  value: number;
  recorded_at: string;
};

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`${path} → ${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

export function getLiveGauges(): Promise<GaugeFeatureCollection> {
  return get("/api/gauges/live");
}

// v2: added measurement field to GaugeProperties (multi-gauge support)
export const SNAPSHOT_CACHE_KEY = "watermap:snapshot:v2";
const SNAPSHOT_CACHE_TTL = 15 * 60 * 1000; // 15 minutes

export async function getLiveGaugesCached(): Promise<GaugeFeatureCollection> {
  try {
    const raw = localStorage.getItem(SNAPSHOT_CACHE_KEY);
    if (raw) {
      const { data, cachedAt } = JSON.parse(raw) as { data: GaugeFeatureCollection; cachedAt: number };
      if (Date.now() - cachedAt < SNAPSHOT_CACHE_TTL) return data;
    }
  } catch {
    // Corrupt or inaccessible localStorage — fall through to fetch
  }
  const data = await getLiveGauges();
  try {
    localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify({ data, cachedAt: Date.now() }));
  } catch {
    // localStorage full or unavailable — ignore
  }
  return data;
}

export function getRiverPolygons(): Promise<GeoJSON.FeatureCollection> {
  return get("/rivers-hb.geojson");
}

// Accepts either a relative hours window or an absolute NZST from/to range.
export function getGaugeHistory(
  siteId: string,
  measurement: string,
  options: number | { from: string; to: string } = 24,
): Promise<{
  site_id: string;
  unit: string;
  readings: GaugeHistoryPoint[];
}> {
  const query =
    typeof options === "number"
      ? `hours=${options}`
      : `from=${encodeURIComponent(options.from)}&to=${encodeURIComponent(options.to)}`;
  return get(`/api/gauges/${encodeURIComponent(siteId)}/history?measurement=${encodeURIComponent(measurement)}&${query}`);
}

export type WellMonthStat = {
  p25: number;
  p75: number;
  sample_count: number;
};

export type WellStatsResult = {
  months: Record<number, WellMonthStat>;
};

export function getWellStats(siteId: string): Promise<WellStatsResult> {
  return get(`/api/gauges/${encodeURIComponent(siteId)}/well-stats`);
}

export function getDailyRainfall(
  siteId: string,
  measurement: string,
): Promise<{ site_id: string; unit: string; days: Array<{ date: string; total: number }> }> {
  return get(`/api/gauges/${encodeURIComponent(siteId)}/daily-rainfall?measurement=${encodeURIComponent(measurement)}`);
}

// Fetch a historical map snapshot at a specific NZST datetime.
// For dates within the 7-day KV window: instant JSON response.
// For older dates: uses SSE streaming so the caller can show a progress bar.
// onProgress receives a 0–100 percentage as each Hilltop batch completes.
export type SnapshotAtResult =
  | { ok: true; snapshot: GaugeFeatureCollection }
  | { ok: false; beyondWindow: boolean; error: string };

export async function getSnapshotAt(
  at: string,
  onProgress?: (pct: number) => void,
): Promise<SnapshotAtResult> {
  const res = await fetch(`/api/gauges/snapshot-at?at=${encodeURIComponent(at)}&stream=1`);

  // Server streams SSE for slow Hilltop fetches; returns JSON directly for the fast KV path.
  if (res.headers.get("content-type")?.includes("text/event-stream")) {
    if (!res.body) return { ok: false, beyondWindow: false, error: "Empty stream" };
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      // SSE blocks are separated by double newline
      const blocks = buf.split("\n\n");
      buf = blocks.pop() ?? "";
      for (const block of blocks) {
        const line = block.split("\n").find((l) => l.startsWith("data: "));
        if (!line) continue;
        const data = JSON.parse(line.slice(6)) as { pct?: number; snapshot?: GaugeFeatureCollection; error?: string };
        if (data.pct !== undefined) onProgress?.(data.pct);
        if (data.snapshot) return { ok: true, snapshot: data.snapshot };
        if (data.error) return { ok: false, beyondWindow: false, error: data.error };
      }
    }
    return { ok: false, beyondWindow: false, error: "Stream ended without a result" };
  }

  // Fast JSON path (KV hit)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    const error = body.error ?? res.statusText;
    return { ok: false, beyondWindow: false, error };
  }
  return { ok: true, snapshot: (await res.json()) as GaugeFeatureCollection };
}
